import { useState } from 'react'
import { Field, FieldProps } from 'formik'
import { DatePicker as ADatePicker } from 'antd'
import { BasicIcon, SuffixIcon, DatePickerWrapper, DatePickerLabel, Required } from './styles'
import CrossIcon from 'globals/icons/cross.svg'
import CheckIcon from 'globals/icons/check.svg'
import moment, { Moment } from 'moment'
import Indication from 'components/Indication'
import frLocale from 'antd/es/date-picker/locale/fr_FR'
import enLocale from 'antd/es/date-picker/locale/en_US'
import 'moment/locale/fr'
import i18next from 'i18next'
import { SetState } from 'globals'
import { CalendarOutlined } from '@ant-design/icons/lib'
import { getLanguage } from 'globals/utils'

interface IProps {
  name: string,
  label?: string,
  placeholder?: string,
  indication?: string,
  hideError?: boolean,
  noBottomPadding?: boolean,
  nbPerLine?: number,
  defaultFocus?: boolean,
  disabled?: boolean,
  onChange?: (date: Moment) => void,
  showErrorOnlyIfEntered?: boolean,
  noFutureDate?: boolean,
  noPastDate?: boolean,
  showToday?: boolean,
  required?: boolean,
  openAt?: Moment
  defaultValue?: Moment
}

const DatePicker = (props: IProps) => {
  const { name, label, placeholder, indication, hideError, noBottomPadding,
    nbPerLine, showErrorOnlyIfEntered, showToday, disabled, openAt, noFutureDate,
    noPastDate, onChange, required, defaultValue } = props
  const [entered, setEntered] = useState(false)

  return (
    <Field name={name}>
      {(props: FieldProps) => {
        const isValid = !props.form.errors[name] && props.field.value !== undefined
        const hasError = !!props.form.errors[name] && (!showErrorOnlyIfEntered || entered)

        return (
          <DatePickerWrapper
            className="ant-input-wrapper"
            hasError={hasError}
            nbPerLine={nbPerLine || 1}
            noBottomPadding={noBottomPadding}
          >
            {label &&
              <DatePickerLabel>
                {required &&
                  <Required>* </Required>
                }
                {label}
              </DatePickerLabel>
            }

            <ADatePicker
              name={name}
              locale={getLanguage(i18next.language) !== 'fr' ? frLocale : enLocale}
              placeholder={placeholder}
              value={props.field.value}
              disabled={disabled}
              onChange={onDateChange(name, props, setEntered, onChange)}
              format="DD/MM/YYYY"
              disabledDate={noFutureDate ? disableFutureDates : noPastDate ? disablePastDates : undefined}
              showToday={showToday}
              defaultPickerValue={openAt}
              defaultValue={defaultValue}
              suffixIcon={(!showErrorOnlyIfEntered || entered) ?
                <SuffixIcon
                  alt={'state-' + name}
                  src={isValid ? CheckIcon : CrossIcon}
                /> :
                <BasicIcon>
                  <CalendarOutlined />
                </BasicIcon>
              }
            />

            {(indication || (props.form.errors[name] && !hideError)) &&
              <Indication
                hasError={hasError}
              >
                {
                (props.form.errors[name] && (!showErrorOnlyIfEntered || entered)) ?
                props.form.errors[name] :
                indication
                }
              </Indication>
            }
          </DatePickerWrapper>
        )
      }}
    </Field>
  )
}

const onDateChange = (
    name: string,
    fieldProps: FieldProps,
    setEntered: SetState<boolean>,
    onChange?: (date: Moment) => void
) => (date: Moment | null) => {
  if (date) {
    if (onChange) {
      onChange(date)
    }

    setEntered(true)

    date.set('hour', 14)

    fieldProps.form.setFieldValue(name, date)
  }
}

const disableFutureDates = (current: Moment | undefined) =>
  current ? current.isAfter(moment(), 'date') : false

const disablePastDates = (current: Moment | undefined) =>
  current ? current.isBefore(moment(), 'date') : false

DatePicker.defaultProps = {
  hideError: false,
  noBottomPadding: false,
  nbPerLine: 1,
  defaultFocus: false,
  disabled: false,
  showErrorOnlyIfEntered: true,
  noFutureDate: false,
  noPastDate: false,
  showToday: false
}

export default DatePicker
